import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Send, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

type UserChat = {
  user_id: string;
  email: string | null;
  phone: string | null;
  unread_count: number;
};

type Message = {
  id: string;
  sender_role: string;
  message: string;
  created_at: string;
  is_read: boolean;
};

export default function AdminChat() {
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserChat[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser);
      markAsRead(selectedUser);

      const channel = supabase
        .channel(`admin_chat_${selectedUser}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `user_id=eq.${selectedUser}`,
        }, (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
          markAsRead(selectedUser);
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, phone')
        .eq('role', 'user');

      if (error) throw error;

      const usersWithUnread = await Promise.all(
        (data || []).map(async (user) => {
          const { count } = await supabase
            .from('chat_messages')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('sender_role', 'user')
            .eq('is_read', false);

          return {
            user_id: user.id,
            email: user.email,
            phone: user.phone,
            unread_count: count || 0,
          };
        })
      );

      setUsers(usersWithUnread);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const markAsRead = async (userId: string) => {
    try {
      await supabase
        .from('chat_messages')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('sender_role', 'user')
        .eq('is_read', false);

      fetchUsers();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          user_id: selectedUser,
          sender_role: 'admin',
          message: newMessage.trim(),
        });

      if (error) throw error;
      setNewMessage('');
      fetchMessages(selectedUser);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="md:col-span-1">
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <div className="space-y-2 p-4">
              {users.map((user) => (
                <Button
                  key={user.user_id}
                  variant={selectedUser === user.user_id ? 'default' : 'outline'}
                  className="w-full justify-between"
                  onClick={() => setSelectedUser(user.user_id)}
                >
                  <span className="truncate">{user.email || user.phone}</span>
                  {user.unread_count > 0 && (
                    <Badge variant="destructive">{user.unread_count}</Badge>
                  )}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="md:col-span-2 h-[600px] flex flex-col">
        <CardHeader>
          <CardTitle>
            {selectedUser ? (
              users.find((u) => u.user_id === selectedUser)?.email ||
              users.find((u) => u.user_id === selectedUser)?.phone
            ) : (
              'Select a user'
            )}
          </CardTitle>
        </CardHeader>
        {selectedUser ? (
          <CardContent className="flex-1 flex flex-col p-0">
            <ScrollArea className="flex-1 px-6">
              <div className="space-y-4 py-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        msg.sender_role === 'admin'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {format(new Date(msg.created_at), 'p')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <form onSubmit={handleSend} className="p-4 border-t flex gap-2">
              <Input
                placeholder={t('typeMessage')}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={loading}
              />
              <Button type="submit" size="icon" disabled={loading || !newMessage.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        ) : (
          <CardContent className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select a user to start chatting</p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}